/**
 * Central configuration for the Investment Portal.
 * All domain constants live here — change once, applied everywhere.
 */

// ─── Investment Rules ─────────────────────────────────────────────────────────

/** Minimum single investment amount (€) */
export const MIN_INVESTMENT = 1;

/** Maximum single investment amount (€) */
export const MAX_INVESTMENT = 10;

/** Investment must be a multiple of this value */
export const INVESTMENT_STEP = 1;

// ─── Revenue Model Defaults ───────────────────────────────────────────────────

/** Default modeled annual revenue used as the starting value (€) */
export const DEFAULT_ANNUAL_REVENUE = 300000;

/** Default percentage of annual revenue distributed to investors */
export const DEFAULT_PAYOUT_PERCENTAGE = 60;

/** Minimum selectable payout percentage (slider lower bound, %) */
export const PAYOUT_PERCENTAGE_MIN = 50;

/** Maximum selectable payout percentage (slider upper bound, %) */
export const PAYOUT_PERCENTAGE_MAX = 70;

// ─── Coefficient Levels ───────────────────────────────────────────────────────
/**
 * Benefit multipliers applied to investor capital at each cumulative threshold.
 * `threshold` is the upper bound (inclusive) of the cumulative total to which
 * the corresponding `coefficient` applies.
 */
export const COEFFICIENT_LEVELS = [
    { threshold: 10,  coefficient: 5   },
    { threshold: 20,  coefficient: 4   },
    { threshold: 30,  coefficient: 3   },
    { threshold: 40,  coefficient: 2   },
    { threshold: 100, coefficient: 1.5 }
];

// ─── Investment Level Table ───────────────────────────────────────────────────
/**
 * Extended level definition displayed by InvestmentOverviewTable.
 * `investment_amount` = capacity of this tier (threshold − previous threshold).
 * `total_payout`      = maximum payout obligation for this tier.
 * Must stay in sync with COEFFICIENT_LEVELS above.
 */
export const INVESTMENT_LEVEL_TABLE = [
    { threshold: 10,  investment_amount: 10, coefficient: 5,   total_payout: 50  },
    { threshold: 20,  investment_amount: 10, coefficient: 4,   total_payout: 40  },
    { threshold: 30,  investment_amount: 10, coefficient: 3,   total_payout: 30  },
    { threshold: 40,  investment_amount: 10, coefficient: 2,   total_payout: 20  },
    { threshold: 100, investment_amount: 60, coefficient: 1.5, total_payout: 90  }
];

/** Maximum total investment capacity — last tier's threshold (€) */
export const INVESTMENT_CAPACITY = INVESTMENT_LEVEL_TABLE[INVESTMENT_LEVEL_TABLE.length - 1].threshold;

/** Maximum total payout obligation across all tiers — sum of total_payout, in table units */
export const TOTAL_PAYOUT_REQUIRED = INVESTMENT_LEVEL_TABLE.reduce((s, l) => s + l.total_payout, 0);

// ─── Calculator Quick-Select Presets ─────────────────────────────────────────
/** Preset investment amounts shown as quick-pick buttons in the calculator. */
export const INVESTMENT_PRESETS = [
    { value: 1,  label: 'Skúška'     },
    { value: 2,  label: 'Základná'   },
    { value: 5,  label: 'Odporúčaná' },
    { value: 10, label: 'Maximálna'  }
];

// ─── Revenue Forecast Defaults ────────────────────────────────────────────────
/** Default input values for the RevenueForecastTable model. */
export const FORECAST_DEFAULTS = {
    pricePerMonth: 1,
    payoutPercent: DEFAULT_PAYOUT_PERCENTAGE,
    slovakiaUsers: { 1: 100000,    2: 300000,    3: 500000     },
    globalUsers:   { 1: 1000000,  2: 10000000, 3: 100000000 },
    fillFactors:   { 1: 30,         2: 30,          3: 30          }
};

// ─── Company / Platform Details ───────────────────────────────────────────────
/** All references to company and platform identity across contracts, dialogs, and payment info. */
export const COMPANY = {
    name:         'VB GUARD s.r.o.',
    ico:          '51 851 491',
    address:      'Páričkova 5, 821 08 Bratislava',
    responsible:  'konateľ, Boris Wagner',
    iban:         'SK98 8330 0000 0091 1199 1119',
    email:        'investiciafichee@gmail.com',
    website:      'www.fmedia.top',
    platformName: 'FICHEE MEDIA',
    paymentNote:  'investicia fichee'
};

// ─── Project / Platform Constants ─────────────────────────────────────────────

/** Number of Fichee projects covered by this investment programme */
export const PROJECT_COUNT = 10;

/** Total startup capital target across all projects (€) */
export const INVESTMENT_TARGET_CAPITAL = 10000000;

/**
 * Scale factor: each unit in the tier table represents this many real euros.
 * Derived from INVESTMENT_TARGET_CAPITAL / INVESTMENT_CAPACITY.
 */
export const UNIT_SCALE = INVESTMENT_TARGET_CAPITAL / INVESTMENT_CAPACITY;

/** Maximum total payout obligation in actual euros (TOTAL_PAYOUT_REQUIRED × UNIT_SCALE) */
export const TOTAL_PAYOUT_EUROS = TOTAL_PAYOUT_REQUIRED * UNIT_SCALE;

// ─── Fallback Projects ────────────────────────────────────────────────────────
/** Displayed in RevenueForecastTable when the API returns no project data. */
export const FALLBACK_PROJECTS = [
    { order: 1,  name: 'FICHEE TAXI'                              },
    { order: 2,  name: 'FICHEE RENTAL + SHARING'                  },
    { order: 3,  name: 'FICHEE KURIÉR'                            },
    { order: 4,  name: 'FICHEE GUARD'                             },
    { order: 5,  name: 'Predaj z dvora'                           },
    { order: 6,  name: 'Holidaydom'                               },
    { order: 7,  name: 'Slovensko investuje'                      },
    { order: 8,  name: 'Loch PUB'                                 },
    { order: 9,  name: 'FICHEE MEDIA'                             },
    { order: 10, name: 'FICHEE - Globálny - Bez hraníc - Utajený' }
];
